import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import CtaBanner from "../components/CtaBanner";
import { stripHtml } from "../lib/html";
import { type } from "../lib/typography";
import { useCertificationsPage, usePageBanner } from "../api/queries";
import { CertificateThumbnail, CertificateLightbox, getCertificatePdfUrl } from "../components/CertificateMedia";
import {
  Award,
  Calendar,
  Building2,
  Eye,
  Search,
} from "lucide-react";

function CertificateCard({ cert, onView }) {
  const pdfUrl = getCertificatePdfUrl(cert);
  const cardClass =
    "group text-left w-full bg-white rounded-xl border border-slate-200 hover:border-[#1e6fcf] hover:shadow-lg transition-all duration-300 overflow-hidden";

  const content = (
    <>
      <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
        <CertificateThumbnail cert={cert} className="w-full h-full" />
        {cert.category && (
        <div className="absolute top-3 left-3">
          <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded bg-white/95 text-[#1758a8] shadow">
            {cert.category}
          </span>
        </div>
        )}
        <div className="absolute inset-0 z-10 bg-[#0a1a2f]/0 group-hover:bg-[#0a1a2f]/45 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
          <span className="inline-flex items-center gap-2 bg-white text-[#0a1a2f] font-bold text-sm px-4 py-2 rounded-md shadow-lg">
            <Eye className="w-4 h-4" />
            {pdfUrl ? "View PDF" : "View Certificate"}
          </span>
        </div>
      </div>
      <div className="p-5">
        <h3 className={`${type.cardTitle} text-[#0a1a2f] group-hover:text-[#1758a8] transition-colors line-clamp-2`}>
          {cert.title}
        </h3>
        {(cert.issuer || cert.year) && (
        <div className={`mt-3 flex flex-wrap gap-x-4 gap-y-1 ${type.meta}`}>
          {cert.issuer && (
          <span className="flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-[#1e6fcf]" /> {cert.issuer}
          </span>
          )}
          {cert.year && (
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-[#1e6fcf]" /> {cert.year}
          </span>
          )}
        </div>
        )}
      </div>
    </>
  );

  if (pdfUrl) {
    return (
      <a
        href={pdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cardClass}
      >
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={() => onView(cert)} className={cardClass}>
      {content}
    </button>
  );
}

export default function CertificationsPage() {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [viewCert, setViewCert] = useState(null);
  const { data, isLoading: loading } = useCertificationsPage();
  const { banner: certificateBanner, isLoading: bannerLoading } = usePageBanner("certificate");

  const pageHeader = data?.header ?? null;
  const certifications = data?.items ?? [];

  const categories = useMemo(() => {
    const cats = [...new Set(certifications.map((c) => c.category).filter(Boolean))];
    return cats.length > 0 ? ["All", ...cats] : ["All"];
  }, [certifications]);

  const showCategoryFilter = categories.length > 1;

  const visible = useMemo(() => {
    let list = certifications;
    if (filter !== "All") {
      list = list.filter((c) => c.category === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.title?.toLowerCase().includes(q) ||
          c.issuer?.toLowerCase().includes(q) ||
          c.description?.toLowerCase().includes(q) ||
          stripHtml(c.description)?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [certifications, filter, search]);

  return (
    <>
      <PageHeader
        title={
          pageHeader?.title ||
          certificateBanner?.title ||
          "Certifications"
        }
        breadcrumb={pageHeader?.title || certificateBanner?.title || "Certifications"}
        image={certificateBanner?.image}
        isLoading={bannerLoading}
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-10">
            <SectionTitle
              eyebrow={pageHeader?.tagline || "Recognition"}
              title="Our Certificates & Accreditations"
              subtitle="Click any certificate to view the full document."
            />
          </div>

          <div className={`flex flex-col gap-4 mb-8 ${showCategoryFilter ? "md:flex-row" : ""}`}>
            <div className={`relative flex-1 ${showCategoryFilter ? "max-w-md" : "max-w-lg"}`}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search certificates..."
                className="w-full pl-10 pr-4 py-2.5 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e6fcf]/30 focus:border-[#1e6fcf]"
              />
            </div>
            {showCategoryFilter && (
            <div className="inline-flex flex-wrap bg-slate-100 p-1.5 rounded-lg gap-1">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${
                    filter === cat
                      ? "bg-[#1e6fcf] text-white shadow-md"
                      : "text-slate-600 hover:text-[#0a1a2f]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            )}
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="rounded-xl border border-slate-200 overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-slate-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : certifications.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-xl border border-dashed border-slate-200">
              <Award className="w-12 h-12 text-[#1e6fcf]/40 mx-auto mb-4" />
              <p className="text-slate-600 font-semibold">No certificates available yet.</p>
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-xl border border-dashed border-slate-200">
              <Award className="w-12 h-12 text-[#1e6fcf]/40 mx-auto mb-4" />
              <p className="text-slate-600 font-semibold">No certificates match your search.</p>
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setFilter("All");
                }}
                className="mt-4 text-sm font-bold text-[#1758a8] hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((cert) => (
                <CertificateCard key={cert.id} cert={cert} onView={setViewCert} />
              ))}
            </div>
          )}

          <p className="mt-10 text-center text-sm text-slate-500">
            Looking for project portfolio?{" "}
            <Link to="/projects" className="font-bold text-[#1758a8] hover:underline">
              View our projects
            </Link>
          </p>
        </div>
      </section>

      <CtaBanner />

      <CertificateLightbox
        cert={viewCert}
        open={Boolean(viewCert)}
        onClose={() => setViewCert(null)}
      />
    </>
  );
}
